// =============================================================================
// BP-05-03: Calendar Frontend - AppointmentDetailPage
// =============================================================================

import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { format } from 'date-fns';
import {
  ArrowLeft,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Calendar,
  Clock,
  MapPin,
  Video,
  User,
  FileText,
  HelpCircle,
  Bell,
  Repeat,
  MoreVertical,
} from 'lucide-react';
import {
  AppointmentCard,
  PrepChecklist,
  QuestionsList,
} from '../components';
import {
  useAppointment,
  useDeleteAppointment,
  useCompleteAppointment,
  useCancelAppointment,
  useUpdateChecklistItem,
  useUpdateQuestion,
  useAddChecklistItem,
  useAddQuestion,
} from '../hooks';
import {
  AppointmentStatus,
  APPOINTMENT_TYPE_CONFIG,
  LOCATION_TYPE_CONFIG,
} from '../types';
import { cn } from '@/core/lib/utils';

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function AppointmentDetailPage() {
  const { familyId, appointmentId } = useParams<{
    familyId: string;
    appointmentId: string;
  }>();
  const navigate = useNavigate();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [completeNotes, setCompleteNotes] = useState('');
  const [followUpNeeded, setFollowUpNeeded] = useState(false);
  const [followUpNotes, setFollowUpNotes] = useState('');

  // Fetch appointment
  const { data: appointment, isLoading, error } = useAppointment(appointmentId || '');

  // Mutations
  const deleteMutation = useDeleteAppointment();
  const completeMutation = useCompleteAppointment();
  const cancelMutation = useCancelAppointment();
  const updateChecklistMutation = useUpdateChecklistItem();
  const addChecklistMutation = useAddChecklistItem();
  const updateQuestionMutation = useUpdateQuestion();
  const addQuestionMutation = useAddQuestion();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage" />
      </div>
    );
  }

  if (error || !appointment) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <p className="text-warm-gray mb-4">Appointment not found</p>
          <Link
            to={`/family/${familyId}/calendar`}
            className="text-sage hover:underline"
          >
            Back to Calendar
          </Link>
        </div>
      </div>
    );
  }

  const startDate = new Date(appointment.startsAt);
  const endDate = new Date(appointment.endsAt);
  const typeConfig = APPOINTMENT_TYPE_CONFIG[appointment.appointmentType];
  const locationConfig = LOCATION_TYPE_CONFIG[appointment.locationType];
  const isActive = appointment.status !== AppointmentStatus.CANCELLED &&
    appointment.status !== AppointmentStatus.COMPLETED;

  // Handlers
  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync({ appointmentId: appointment.id });
      navigate(`/family/${familyId}/calendar`);
    } catch (error) {
      console.error('Failed to delete:', error);
    }
  };

  const handleComplete = async () => {
    try {
      await completeMutation.mutateAsync({
        appointmentId: appointment.id,
        notes: completeNotes,
        followUpNeeded,
        followUpNotes,
      });
      setShowCompleteModal(false);
    } catch (error) {
      console.error('Failed to complete:', error);
    }
  };

  const handleCancel = async () => {
    try {
      await cancelMutation.mutateAsync({
        appointmentId: appointment.id,
        reason: cancelReason,
      });
      setShowCancelModal(false);
    } catch (error) {
      console.error('Failed to cancel:', error);
    }
  };

  const handleChecklistToggle = (itemId: string, completed: boolean) => {
    updateChecklistMutation.mutate({
      appointmentId: appointment.id,
      itemId,
      completed,
    });
  };

  const handleAddChecklistItem = (item: string) => {
    addChecklistMutation.mutate({
      appointmentId: appointment.id,
      item,
    });
  };

  const handleAnswerQuestion = (questionId: string, answer: string) => {
    updateQuestionMutation.mutate({
      appointmentId: appointment.id,
      questionId,
      answer,
    });
  };

  const handleAddQuestion = (question: string) => {
    addQuestionMutation.mutate({
      appointmentId: appointment.id,
      question,
    });
  };

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="bg-white border-b border-warm-gray/10 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to={`/family/${familyId}/calendar`}
                className="p-2 hover:bg-cream rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-navy" />
              </Link>
              <div>
                <h1 className="text-xl font-display font-semibold text-navy">
                  {appointment.title}
                </h1>
                <p className="text-sm text-warm-gray">
                  {format(startDate, 'EEEE, MMMM d, yyyy')}
                </p>
              </div>
            </div>

            {isActive && (
              <div className="flex items-center gap-2">
                <Link
                  to={`/family/${familyId}/appointments/${appointment.id}/edit`}
                  className="flex items-center gap-2 px-4 py-2 bg-cream text-navy rounded-lg hover:bg-cream-dark transition-colors"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </Link>
                <div className="relative">
                  <button className="p-2 hover:bg-cream rounded-lg transition-colors">
                    <MoreVertical className="w-5 h-5 text-navy" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Appointment Card */}
            <AppointmentCard
              appointment={appointment}
              variant="detailed"
              showActions={isActive}
              onEdit={() => navigate(`/family/${familyId}/appointments/${appointment.id}/edit`)}
              onComplete={() => setShowCompleteModal(true)}
              onCancel={() => setShowCancelModal(true)}
            />

            {/* Prep Checklist */}
            <div className="bg-white rounded-xl p-6 shadow-soft">
              <h3 className="text-lg font-display font-semibold text-navy mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-sage" />
                Prep Checklist
              </h3>
              <PrepChecklist
                items={appointment.prepChecklist}
                onToggle={isActive ? handleChecklistToggle : undefined}
                onAdd={isActive ? handleAddChecklistItem : undefined}
                readOnly={!isActive}
              />
            </div>

            {/* Questions to Ask */}
            <div className="bg-white rounded-xl p-6 shadow-soft">
              <h3 className="text-lg font-display font-semibold text-navy mb-4 flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-sage" />
                Questions to Ask
              </h3>
              <QuestionsList
                questions={appointment.questionsToAsk}
                onAnswer={handleAnswerQuestion}
                onAdd={isActive ? handleAddQuestion : undefined}
                readOnly={!isActive}
              />
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Info */}
            <div className="bg-white rounded-xl p-6 shadow-soft space-y-4">
              <h3 className="font-medium text-navy">Quick Info</h3>

              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-warm-gray" />
                  <span>{format(startDate, 'MMMM d, yyyy')}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-warm-gray" />
                  <span>
                    {appointment.isAllDay
                      ? 'All Day'
                      : `${format(startDate, 'h:mm a')} - ${format(endDate, 'h:mm a')}`}
                  </span>
                </div>
                {appointment.providerName && (
                  <div className="flex items-center gap-3">
                    <User className="w-4 h-4 text-warm-gray" />
                    <span>{appointment.providerName}</span>
                  </div>
                )}
                {appointment.isRecurring && (
                  <div className="flex items-center gap-3 text-purple-600">
                    <Repeat className="w-4 h-4" />
                    <span>Recurring appointment</span>
                  </div>
                )}
              </div>
            </div>

            {/* Reminders */}
            <div className="bg-white rounded-xl p-6 shadow-soft">
              <h3 className="font-medium text-navy mb-4 flex items-center gap-2">
                <Bell className="w-4 h-4 text-sage" />
                Reminders
              </h3>
              {appointment.reminderMinutes.length > 0 ? (
                <div className="space-y-2">
                  {appointment.reminderMinutes.map((minutes) => (
                    <div
                      key={minutes}
                      className="text-sm text-warm-gray"
                    >
                      {minutes < 60
                        ? `${minutes} minutes before`
                        : minutes < 1440
                        ? `${Math.floor(minutes / 60)} hours before`
                        : `${Math.floor(minutes / 1440)} days before`}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-warm-gray">No reminders set</p>
              )}
            </div>

            {/* Actions */}
            {isActive && (
              <div className="bg-white rounded-xl p-6 shadow-soft space-y-3">
                <h3 className="font-medium text-navy mb-4">Actions</h3>
                <button
                  onClick={() => setShowCompleteModal(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors"
                >
                  <CheckCircle className="w-4 h-4" />
                  Mark as Completed
                </button>
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-cream text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                >
                  <XCircle className="w-4 h-4" />
                  Cancel Appointment
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Complete Modal */}
      {showCompleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-navy/50 backdrop-blur-sm" onClick={() => setShowCompleteModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-lifted w-full max-w-md p-6">
            <h3 className="text-lg font-display font-semibold text-navy mb-4">
              Complete Appointment
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-navy mb-1">
                  Notes (optional)
                </label>
                <textarea
                  value={completeNotes}
                  onChange={(e) => setCompleteNotes(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 bg-cream rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/20"
                  placeholder="Any notes from the appointment..."
                />
              </div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={followUpNeeded}
                  onChange={(e) => setFollowUpNeeded(e.target.checked)}
                  className="rounded text-sage focus:ring-sage"
                />
                <span className="text-sm">Follow-up needed</span>
              </label>
              {followUpNeeded && (
                <textarea
                  value={followUpNotes}
                  onChange={(e) => setFollowUpNotes(e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2 bg-cream rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/20"
                  placeholder="Follow-up details..."
                />
              )}
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCompleteModal(false)}
                className="flex-1 px-4 py-2.5 text-navy font-medium rounded-lg hover:bg-cream transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleComplete}
                disabled={completeMutation.isPending}
                className="flex-1 px-4 py-2.5 bg-sage text-white font-medium rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-50"
              >
                {completeMutation.isPending ? 'Completing...' : 'Complete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-navy/50 backdrop-blur-sm" onClick={() => setShowCancelModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-lifted w-full max-w-md p-6">
            <h3 className="text-lg font-display font-semibold text-navy mb-4">
              Cancel Appointment
            </h3>
            <div>
              <label className="block text-sm font-medium text-navy mb-1">
                Reason (optional)
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 bg-cream rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/20"
                placeholder="Why is this appointment being cancelled?"
              />
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 px-4 py-2.5 text-navy font-medium rounded-lg hover:bg-cream transition-colors"
              >
                Keep Appointment
              </button>
              <button
                onClick={handleCancel}
                disabled={cancelMutation.isPending}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {cancelMutation.isPending ? 'Cancelling...' : 'Cancel Appointment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-navy/50 backdrop-blur-sm" onClick={() => setShowDeleteConfirm(false)} />
          <div className="relative bg-white rounded-2xl shadow-lifted w-full max-w-md p-6">
            <h3 className="text-lg font-display font-semibold text-navy mb-2">
              Delete Appointment
            </h3>
            <p className="text-warm-gray mb-6">
              Are you sure you want to delete this appointment? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2.5 text-navy font-medium rounded-lg hover:bg-cream transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AppointmentDetailPage;
